import React, { useRef, useState } from 'react';
import axios from 'axios';

import { colorPalette, useStyles } from '~/assets/styles';
import WithConfirm from '~/components/ConfirmModal';
import { LoadingSwitch, SortableTable } from '~/components/core';
import Button from '~/components/core/Atomic/Buttons/Button';
import IconButton from '~/components/core/Atomic/Buttons/IconButton';
import CircularProgress from '~/components/core/Atomic/CircularProgress/CircularProgress';
import Typography from '~/components/core/Atomic/Typography';
import { CopyToClipboardIcon, EyeIcon, PencilIcon, TrashIcon } from '~/components/icons';
import { useSysconfig } from '~/components/SystemConfiguration/SystemConfigurationScreen';
import type { HookEndpointResponse } from '~/server_shared/openapi/data-contracts';
import { reportAxiosError } from '~/Utils';

import HookEndpointDialog from './HookEndpointDialog';
import useHookEndpoints, { getHookEndpointsUrl } from './useHookEndpoints';

const SECRET_DOTS = '• '.repeat(40);

const SecretCell: React.FC<{ endpointKey: string; baseUrl: string }> = ({ endpointKey, baseUrl }) => {
  const { classes } = useStyles();
  const [isLoading, setIsLoading] = useState(false);
  const [isRevealed, setIsRevealed] = useState(false);
  const secretRef = useRef<string>();

  const fetchSecret = async () => {
    if (!secretRef.current) {
      setIsLoading(true);
      try {
        const { data } = await axios.get(`${baseUrl}/${endpointKey}/secret`);
        secretRef.current = data;
      } catch (error) {
        await reportAxiosError(error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleToggle = async () => {
    await fetchSecret();
    setIsRevealed((prev) => !prev);
  };

  const handleCopy = async () => {
    if (isRevealed && secretRef.current) {
      await navigator.clipboard.writeText(secretRef.current);
    }
  };

  if (isLoading) {
    return <CircularProgress size={20} className={classes.inlineEditIcon} />;
  }

  return (
    <div className="flex items-center">
      <Typography variant="body1" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {isRevealed ? secretRef.current : SECRET_DOTS}
      </Typography>
      <IconButton className={classes.tableActionsIcon} onClick={handleToggle}>
        <EyeIcon iconColor={isRevealed ? colorPalette.status.canceled : undefined} />
      </IconButton>
      {isRevealed && (
        <IconButton className={classes.tableActionsIcon} onClick={handleCopy}>
          <CopyToClipboardIcon />
        </IconButton>
      )}
    </div>
  );
};

const HookEndpoints: React.FC = () => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const { organization } = useSysconfig();
  const { classes } = useStyles();
  const baseUrl = getHookEndpointsUrl(organization.id);

  const { isLoading, isError, data: endpoints, reloadData } = useHookEndpoints(organization.id);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEndpoint, setEditingEndpoint] = useState<HookEndpointResponse | null>(null);

  const handleDelete = async (key: string) => {
    try {
      await axios.delete(`${baseUrl}/${key}`);
      await reloadData();
    } catch (error) {
      await reportAxiosError(error);
    }
  };

  const handleDialogClose = async () => {
    setDialogOpen(false);
    setEditingEndpoint(null);
    await reloadData();
  };

  const columns = [
    { id: 'name', label: 'Name' },
    { id: 'key', label: 'Key' },
    { id: 'callback_url', label: 'Callback URL' },
    { id: 'timeout_seconds', label: 'Timeout (s)', width: '80px' },
    {
      id: 'secret',
      label: 'Secret',
      width: '300px',
      disableSort: true,
      specialCell: (row: HookEndpointResponse) => <SecretCell endpointKey={row.key} baseUrl={baseUrl} />,
    },
    {
      id: 'actions',
      label: '',
      width: '80px',
      disableSort: true,
      specialCell: (row: HookEndpointResponse) => (
        <div className="flex gap-4">
          <IconButton
            className={classes.tableActionsIcon}
            onClick={() => {
              setEditingEndpoint(row);
              setDialogOpen(true);
            }}
          >
            <PencilIcon />
          </IconButton>
          <WithConfirm
            title="Are you sure?"
            contentText={`Are you sure you want to delete hook endpoint "${row.name}"?`}
            primaryButtonName="Delete"
            shouldCloseOnPrimary
          >
            <IconButton className={classes.tableActionsIcon} onClick={() => handleDelete(row.key)}>
              <TrashIcon />
            </IconButton>
          </WithConfirm>
        </div>
      ),
    },
  ];

  return (
    <div className="p-20">
      <div className="mb-12 flex items-center justify-between">
        <h2 className="text-lg font-semibold">Hook Endpoints</h2>
        <Button variant="contained" color="primary" onClick={() => setDialogOpen(true)}>
          Add Endpoint
        </Button>
      </div>
      <LoadingSwitch isLoading={isLoading} isError={isError}>
        <SortableTable columns={columns} rows={endpoints || []} />
      </LoadingSwitch>
      {dialogOpen && (
        <HookEndpointDialog
          organizationId={organization.id}
          existingEndpoint={editingEndpoint}
          onClose={handleDialogClose}
        />
      )}
    </div>
  );
};

export default HookEndpoints;
